// App-wide constants
import type { Job, DocumentType, PricingTier } from '@formmitra/shared';

export const LANGUAGES = [
  { code: 'mr', name: 'Marathi',  nativeName: 'मराठी',  flag: '🇮🇳' },
  { code: 'hi', name: 'Hindi',    nativeName: 'हिंदी',   flag: '🇮🇳' },
  { code: 'en', name: 'English',  nativeName: 'English', flag: '🇬🇧' },
] as const;

export const JOB_CATEGORIES = [
  { value: 'all',         label: 'All',         color: 'gray' },
  { value: 'maharashtra', label: 'Maharashtra', color: 'orange' },
  { value: 'state',       label: 'State',       color: 'blue' },
  { value: 'central',     label: 'Central',     color: 'green' },
] as const;

export const SOURCE_LABELS: Record<string, string> = {
  mahabharti: 'Mahabharti',
  mpsc:       'MPSC',
  mahait:     'MahaIT',
  ibps:       'IBPS',
  ssc:        'SSC',
  upsc:       'UPSC',
  mahaother:  'Other',
};

export const DOC_TYPES: { value: DocumentType; labelMr: string; labelEn: string; required: boolean }[] = [
  { value: 'aadhaar',          labelMr: 'आधार कार्ड',       labelEn: 'Aadhaar Card',       required: true },
  { value: 'pan',              labelMr: 'PAN कार्ड',         labelEn: 'PAN Card',           required: false },
  { value: 'tenth_marksheet',  labelMr: '१०वी मार्कशीट',    labelEn: '10th Marksheet',     required: true },
  { value: 'twelfth_marksheet',labelMr: '१२वी मार्कशीट',    labelEn: '12th Marksheet',     required: true },
  { value: 'graduation_certificate', labelMr: 'पदवी प्रमाणपत्र', labelEn: 'Graduation',   required: false },
  { value: 'caste_certificate',labelMr: 'जात प्रमाणपत्र',     labelEn: 'Caste Certificate',  required: false },
  { value: 'domicile_certificate', labelMr: 'अधिवास',         labelEn: 'Domicile',           required: false },
  { value: 'photo',            labelMr: 'फोटो',              labelEn: 'Photo',              required: true },
  { value: 'signature',        labelMr: 'स्वाक्षरी',          labelEn: 'Signature',          required: true },
];

// Mock jobs for development (before scraper is live)
export const MOCK_JOBS: Job[] = [
  {
    id: 'ssc-cgl-2025',
    title: 'SSC CGL 2025',
    titleEn: 'SSC CGL 2025 — Junior Accountant, TA, etc.',
    titleMr: 'SSC CGL 2025 — कनिष्ठ लेखापाल, TA, इ.',
    organization: 'Staff Selection Commission',
    organizationEn: 'Staff Selection Commission',
    organizationMr: 'कर्मचारी निवड आयोग',
    category: 'central',
    posts: 17727,
    salary: '₹25,500 - ₹1,51,100',
    salaryEn: '₹25,500 - ₹1,51,100',
    salaryMr: '₹२५,५०० - ₹१,५१,१००',
    qualification: 'Any Graduate',
    qualificationEn: 'Any Graduate',
    qualificationMr: 'कोणतीही पदवी',
    ageMin: 18, ageMax: 32,
    lastDate: '2026-07-15',
    applyUrl: 'https://ssc.nic.in',
    officialSite: 'https://ssc.nic.in',
    source: 'ssc',
    scrapedAt: new Date().toISOString(),
    isActive: true,
    verificationMethods: [
      { type: 'aadhaar_otp', badge: 'Aadhaar OTP', description: 'Aadhaar OTP required at submission' },
      { type: 'photo', badge: 'Photo Upload', description: 'Scanned photo upload' },
      { type: 'signature', badge: 'Signature', description: 'Scanned signature upload' },
    ],
  },
  {
    id: 'mpsc-rajyaseva-2025',
    title: 'MPSC Rajyaseva 2025',
    titleEn: 'MPSC State Services Prelims Exam 2025',
    titleMr: 'MPSC राज्यसेवा पूर्व परीक्षा 2025',
    organization: 'Maharashtra Public Service Commission',
    organizationEn: 'Maharashtra Public Service Commission',
    organizationMr: 'महाराष्ट्र लोकसेवा आयोग',
    category: 'maharashtra',
    posts: 274,
    salary: '₹56,100 - ₹1,77,500',
    salaryEn: '₹56,100 - ₹1,77,500',
    salaryMr: '₹५६,१०० - ₹१,७७,५००',
    qualification: 'Graduate',
    qualificationEn: 'Graduate',
    qualificationMr: 'पदवीधर',
    ageMin: 19, ageMax: 38,
    lastDate: '2026-06-30',
    applyUrl: 'https://mpsc.gov.in',
    officialSite: 'https://mpsc.gov.in',
    state: 'Maharashtra',
    source: 'mpsc',
    scrapedAt: new Date().toISOString(),
    isActive: true,
    verificationMethods: [
      { type: 'aadhaar_otp', badge: 'Aadhaar OTP', description: 'Aadhaar OTP required' },
      { type: 'face', badge: 'Face Verify', description: 'Face verification' },
    ],
  },
  {
    id: 'mahabharti-police-2025',
    title: 'Maharashtra Police Bharti 2025',
    titleEn: 'Maharashtra Police Recruitment 2025',
    titleMr: 'महाराष्ट्र पोलीस भरती २०२५',
    organization: 'Maharashtra Police',
    organizationEn: 'Maharashtra Police',
    organizationMr: 'महाराष्ट्र पोलीस',
    category: 'maharashtra',
    posts: 18000,
    salary: '₹21,700 - ₹69,100',
    salaryEn: '₹21,700 - ₹69,100',
    salaryMr: '₹२१,७०० - ₹६९,१००',
    qualification: '12th Pass',
    qualificationEn: '12th Pass',
    qualificationMr: '१२वी उत्तीर्ण',
    ageMin: 18, ageMax: 31,
    lastDate: '2026-07-01',
    applyUrl: 'https://mahapolice.gov.in',
    officialSite: 'https://mahapolice.gov.in',
    state: 'Maharashtra',
    source: 'mahabharti',
    scrapedAt: new Date().toISOString(),
    isActive: true,
    fee: 450,
    verificationMethods: [
      { type: 'aadhaar_otp', badge: 'Aadhaar', description: 'Aadhaar verification' },
      { type: 'biometric', badge: 'Biometric', description: 'Fingerprint + photo' },
    ],
  },
  {
    id: 'ibps-clerk-2025',
    title: 'IBPS Clerk 2025',
    titleEn: 'IBPS Clerk 2025',
    titleMr: 'IBPS क्लार्क 2025',
    organization: 'Institute of Banking Personnel Selection',
    organizationEn: 'Institute of Banking Personnel Selection',
    organizationMr: 'बँकिंग कर्मचारी निवड संस्था',
    category: 'central',
    posts: 8000,
    salary: '₹29,000 - ₹1,00,000',
    salaryEn: '₹29,000 - ₹1,00,000',
    salaryMr: '₹२९,००० - ₹१,००,०००',
    qualification: 'Any Graduate',
    qualificationEn: 'Any Graduate',
    qualificationMr: 'कोणतीही पदवी',
    ageMin: 20, ageMax: 28,
    lastDate: '2026-07-21',
    applyUrl: 'https://ibps.in',
    officialSite: 'https://ibps.in',
    source: 'ibps',
    scrapedAt: new Date().toISOString(),
    isActive: true,
    fee: 850,
    verificationMethods: [
      { type: 'aadhaar_otp', badge: 'Aadhaar OTP', description: 'Aadhaar OTP required' },
      { type: 'esign', badge: 'eSign', description: 'Digital signature' },
    ],
  },
  {
    id: 'upsc-cse-2025',
    title: 'UPSC Civil Services 2025',
    titleEn: 'UPSC Civil Services 2025',
    titleMr: 'UPSC सिव्हिल सर्व्हिसेस 2025',
    organization: 'Union Public Service Commission',
    organizationEn: 'Union Public Service Commission',
    organizationMr: 'केंद्रीय लोकसेवा आयोग',
    category: 'central',
    posts: 1129,
    salary: '₹56,100 - ₹2,50,000',
    salaryEn: '₹56,100 - ₹2,50,000',
    salaryMr: '₹५६,१०० - ₹२,५०,०००',
    qualification: 'Any Graduate',
    qualificationEn: 'Any Graduate',
    qualificationMr: 'कोणतीही पदवी',
    ageMin: 21, ageMax: 32,
    lastDate: '2026-06-18',
    applyUrl: 'https://upsc.gov.in',
    officialSite: 'https://upsc.gov.in',
    source: 'upsc',
    scrapedAt: new Date().toISOString(),
    isActive: true,
    fee: 100,
    verificationMethods: [
      { type: 'aadhaar_otp', badge: 'Aadhaar', description: 'Aadhaar mandatory' },
      { type: 'photo', badge: 'Photo', description: 'Photo + signature upload' },
    ],
  },
  {
    id: 'maha-it-assistant-2025',
    title: 'MahaIT Assistant 2025',
    titleEn: 'MahaIT Assistant 2025',
    titleMr: 'महाआयटी सहाय्यक २०२५',
    organization: 'Maharashtra Information Technology',
    organizationEn: 'Maharashtra Information Technology',
    organizationMr: 'महाराष्ट्र माहिती तंत्रज्ञान',
    category: 'maharashtra',
    posts: 320,
    salary: '₹35,000 - ₹1,12,000',
    salaryEn: '₹35,000 - ₹1,12,000',
    salaryMr: '₹३५,००० - ₹१,१२,०००',
    qualification: 'BCA/BSc IT',
    qualificationEn: 'BCA/BSc IT',
    qualificationMr: 'BCA/BSc IT',
    ageMin: 18, ageMax: 38,
    lastDate: '2026-07-10',
    applyUrl: 'https://mahaait.gov.in',
    officialSite: 'https://mahaait.gov.in',
    state: 'Maharashtra',
    source: 'mahait',
    scrapedAt: new Date().toISOString(),
    isActive: true,
  },
];

// Pricing tiers (in INR)
export const PRICING_TIERS: PricingTier[] = [
  {
    id: 'free',
    name: 'Free',
    price: 0,
    period: 'forever',
    description: 'Try FormMitra with 3 free form fills',
    features: [
      '3 form fills per month',
      '1 active task at a time',
      'Email support',
      'Basic form fields auto-fill',
    ],
    cta: 'Get Started',
    popular: false,
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 99,
    period: 'month',
    description: 'Unlimited form fills, perfect for active job seekers',
    features: [
      'Unlimited form fills',
      '5 parallel tasks',
      'Priority WhatsApp support',
      'All form fields auto-fill',
      'Document auto-upload',
      'CAPTCHA auto-solve',
    ],
    cta: 'Go Pro',
    popular: true,
  },
  {
    id: 'pro_yearly',
    name: 'Pro Yearly',
    price: 999,
    period: 'year',
    description: 'Save 16% with annual billing',
    features: [
      'Everything in Pro',
      'Save ₹189 per year',
      'Premium support',
      'Early access to new features',
    ],
    cta: 'Save 16%',
    popular: false,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 4999,
    period: 'month',
    description: 'For consultancies and bulk applicants',
    features: [
      'Everything in Pro',
      'Unlimited parallel tasks',
      'API access',
      'Dedicated account manager',
      'Custom form templates',
    ],
    cta: 'Contact Sales',
    popular: false,
  },
];
